import { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { getIncidentsList } from '../../../store/incidentsReducer';
import {
  DateRangePicker,
  generateYearOptions,
  pickerLabels,
  pickerValuesMap,
  useDateRangeFilter,
} from '../../DatePickers/DateRangeFilter';
import {
  FILTER_PARAMS,
  getFilterParams,
  initSettings,
} from './useFiltersUrlState';
import { useSelector } from 'react-redux';

const datePickerValuesMap = {
  custom: 'custom',
  today: 'today',
  mtd: 'mtd',
  ytd: 'ytd',
  last7Days: 7,
  last30Days: 30,
  lastYear: 364,
};

const filterOptionsBase = [
  {
    title: 'Date Range',
    category: 'dateRange',
    renderOption: ({ onChange, value }) => {
      return <DateRange onChange={onChange} value={value} />;
    },
    options: [],
  },
  {
    title: 'Incident Type',
    category: FILTER_PARAMS.incident_type,
    options: [
      'Fire',
      'Medical',
      'Rescue',
      'Hazsit',
      'Noemerg',
      'Lawenforce',
      'Other',
    ],
  },
  { title: 'Status', category: 'status', options: ['Opened', 'Closed'] },
  {
    title: 'NERIS Status',
    category: FILTER_PARAMS.neris_status,
    options: ['Sending', 'Incomplete', 'Complete', 'Waiting', 'Error', 'N/A'],
  },
  {
    title: 'Total Response Time',
    category: FILTER_PARAMS.response_time,
    options: ['<5 Min', '5-10 Min', '10+ Min'],
  },
];

const datePickerPresetOptions = [
  {
    value: pickerValuesMap.custom,
    label: pickerLabels[pickerValuesMap.custom],
  },
  {
    value: pickerValuesMap.last7Days,
    label: pickerLabels[pickerValuesMap.last7Days],
  },
  {
    value: pickerValuesMap.last30Days,
    label: pickerLabels[pickerValuesMap.last30Days],
  },
  {
    value: pickerValuesMap.lastYear,
    label: pickerLabels[pickerValuesMap.lastYear],
  },
  {
    value: pickerValuesMap.today,
    label: pickerLabels[pickerValuesMap.today],
  },
  { value: pickerValuesMap.mtd, label: pickerLabels[pickerValuesMap.mtd] },
  { value: pickerValuesMap.ytd, label: pickerLabels[pickerValuesMap.ytd] },
  ...generateYearOptions(2),
];

const DateRange = ({ onChange, value }) => {
  const dateRangeFilter = useDateRangeFilter({
    dateRangeMap: datePickerValuesMap,
  });

  // binding parent -> DateRangePicker
  //  clear data from the parent to DateRangePicker
  useEffect(() => {
    if (
      !value?.toDate &&
      !value?.fromDate &&
      !!dateRangeFilter.fromDate &&
      !!dateRangeFilter.toDate
    ) {
      dateRangeFilter.onClear();
    }
  }, [value]);

  return (
    <DateRangePicker
      {...dateRangeFilter}
      presetOptions={datePickerPresetOptions}
    />
  );
};

const FilterDrawer = ({
  open,
  onClose,
  paginationModel,
  filters,
  setFilters,
  setParams,
}) => {
  const dispatch = useDispatch();

  const { departmentsList } = useSelector((state) => state.incidentsReducer);

  const filterOptions = useMemo(() => {
    return [
      ...filterOptionsBase,
      {
        title: 'Departments',
        category: 'department',
        options: Object.keys(departmentsList),
        optionsLabels: Object.values(departmentsList),
      },
    ];
  }, [departmentsList]);

  const handleCheckboxChange = (category, value) => {
    const newFilters = { ...filters };

    if (category === 'dateRange') {
      // dateRange is an object, not an array
      newFilters[category] = value || {};
    } else if (
      [
        FILTER_PARAMS.status,
        FILTER_PARAMS.neris_status,
        FILTER_PARAMS.response_time,
        FILTER_PARAMS.department,
      ].includes(category)
    ) {
      newFilters[category] = newFilters[category].includes(value)
        ? []
        : [value]; // Allow unchecking
    } else {
      newFilters[category] = newFilters[category].includes(value)
        ? newFilters[category].filter((v) => v !== value)
        : [...newFilters[category], value];
    }

    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters(initSettings);
  };

  const applyFilterHandler = () => {
    const params = getFilterParams();

    setParams((prev) => {
      // Reset to first page when filters change
      prev.delete('page');
      prev.set('page', '0');

      return prev;
    });

    if (Object.keys(params).length > 0) {
      dispatch(
        getIncidentsList({
          page: 1,
          pageSize: paginationModel.pageSize,
          ...params,
        })
      );
    } else {
      dispatch(
        getIncidentsList({ page: 1, pageSize: paginationModel.pageSize })
      );
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: '0px 4px 4px 0px #0000000F',
          border: '1px solid #0000001F',
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
        },
        '& .MuiBackdrop-root': {
          background: 'transparent',
        },
      }}
    >
      <Box sx={{ width: 320, padding: 2, paddingBottom: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: 16, color: '#000', fontWeight: 600 }}
          >
            Filters
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {filterOptions.map(
            ({ title, category, options, renderOption, optionsLabels }) => (
              <Accordion
                key={category}
                defaultExpanded
                sx={{
                  boxShadow: 'none',
                  borderBottom: '1px solid #F0EBEA',
                  margin: 0,
                  '&.Mui-expanded': { margin: 0 },
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    paddingX: 0,
                    '&.Mui-expanded': { minHeight: 'auto' },
                    margin: 0,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      '&.Mui-expanded': { margin: '12px 0' },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 600,
                    }}
                  >
                    {title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    paddingX: 0,
                  }}
                >
                  {renderOption?.({
                    onChange: (value) => {
                      handleCheckboxChange(category, value);
                    },
                    value:
                      category === 'dateRange'
                        ? filters[category]
                        : filters[category][0],
                  })}
                  {options.map((option, index) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={filters[category].includes(option)}
                          onChange={() =>
                            handleCheckboxChange(category, option)
                          }
                          sx={{
                            color: '#00000099',
                            '&.Mui-checked': { color: 'black' },
                          }}
                        />
                      }
                      label={optionsLabels ? optionsLabels[index] : option}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.87)',
                      }}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            background: '#fff',
            paddingY: '16px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={clearFilters} sx={{ color: 'gray' }}>
            Clear all
          </Button>
          <Button
            onClick={applyFilterHandler}
            variant="outlined"
            sx={{
              borderColor: '#E1E1E1',
              color: '#000',

              '&:hover': {
                borderColor: '#000',
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
